import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/10" }
const _hoisted_3 = {
  key: 0,
  class: "mt-4 flex items-start justify-between text-sm text-gray-600"
}
const _hoisted_4 = {
  key: 0,
  class: "mr-4 font-bold"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  key: 0,
  class: "fixed inset-0 z-[9999]"
}
const _hoisted_7 = { class: "fixed left-4 top-4 z-50 flex items-center space-x-2" }
const _hoisted_8 = { class: "hidden min-w-[3rem] rounded-full bg-black/50 px-2 py-1 text-center font-mono text-xs text-white md:inline-block" }
const _hoisted_9 = { class: "fixed right-4 top-4 z-50 flex items-center space-x-4" }
const _hoisted_10 = ["src", "srcset", "alt"]

import { ref, computed } from "vue";
import { Image } from "~/js/interfaces/media";
import { getClasses } from "~/js/composables/classes";
import { getGlobalTranslation } from "~/js/composables/translations";
import { sanitizeHtml } from "~/js/utils/sanitize-html";

import ImageTeaser from "~/vue/atoms/images/ImageTeaser.vue";
import ButtonPrimary from "~/vue/atoms/buttons/ButtonPrimary.vue";
import Body from "~/vue/atoms/texts/TextBody.vue";

interface Props {
	image: Image;
	caption?: string | null;
	credits?: string | null;
	utilities?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LightboxSingleImage',
  props: {
    image: {},
    caption: { default: null },
    credits: { default: null },
    utilities: { default: "" }
  },
  setup(__props: any) {

const props = __props;

const isOpen = ref(false);
const zoomLevel = ref(1);
const scrollContainer = ref<HTMLElement | null>(null);

const isDown = ref(false);
const hasMoved = ref(false);
const startX = ref(0);
const startY = ref(0);
const scrollLeft = ref(0);
const scrollTop = ref(0);

const classes = ref({
	overlay: {
		position: "absolute inset-0 bg-black/90 backdrop-blur-sm",
		size: "w-full h-full",
	},
});

const openLightbox = () => {
	isOpen.value = true;
	zoomLevel.value = 1;
	document.body.style.overflow = "hidden";
};

const closeLightbox = () => {
	isOpen.value = false;
	document.body.style.overflow = "";
	isDown.value = false;
};

const zoomIn = () => {
	zoomLevel.value += 0.5;
};

const zoomOut = () => {
	if (zoomLevel.value > 1) {
		zoomLevel.value -= 0.5;
	}
};

const imgSrcset = computed(() => {
	return props.image.srcset || props.image.url;
});

const imageStyle = computed(() => {
	if (zoomLevel.value === 1) {
		return {
			maxWidth: "100%",
			maxHeight: "85vh",
			objectFit: "contain" as const,
			cursor: "zoom-in",
		};
	}

	return {
		width: `${zoomLevel.value * 100}vw`,
		maxWidth: "none",
		height: "auto",
		cursor: isDown.value ? "grabbing" : "grab",
	};
});

const sanitizedCaption = computed(() => sanitizeHtml(props.caption));

const toggleZoomClick = (event: MouseEvent) => {
	if (hasMoved.value) return;

	if (zoomLevel.value === 1) {
		zoomLevel.value = 1.5;
	} else {
		zoomLevel.value = 1;
	}
};

const startDrag = (e: MouseEvent) => {
	if (zoomLevel.value <= 1 || !scrollContainer.value) return;

	e.preventDefault();
	isDown.value = true;
	hasMoved.value = false;

	startX.value = e.pageX - scrollContainer.value.offsetLeft;
	startY.value = e.pageY - scrollContainer.value.offsetTop;
	scrollLeft.value = scrollContainer.value.scrollLeft;
	scrollTop.value = scrollContainer.value.scrollTop;
};

const onDrag = (e: MouseEvent) => {
	if (!isDown.value || !scrollContainer.value) return;

	e.preventDefault();

	const x = e.pageX - scrollContainer.value.offsetLeft;
	const y = e.pageY - scrollContainer.value.offsetTop;

	if (Math.abs(x - startX.value) > 5 || Math.abs(y - startY.value) > 5) {
		hasMoved.value = true;
	}

	const walkX = (x - startX.value) * 1.5;
	const walkY = (y - startY.value) * 1.5;

	scrollContainer.value.scrollLeft = scrollLeft.value - walkX;
	scrollContainer.value.scrollTop = scrollTop.value - walkY;
};

const stopDrag = () => {
	isDown.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['group relative', __props.utilities])
  }, [
    _createElementVNode("button", {
      type: "button",
      class: "relative block w-full cursor-zoom-in overflow-hidden rounded-3xl text-left focus:outline-none focus:ring-4 focus:ring-blue-500",
      "aria-label": _unref(getGlobalTranslation)('Maximize'),
      onClick: openLightbox
    }, [
      _createVNode(ImageTeaser, {
        image: __props.image,
        utilities: 'w-full h-auto'
      }, null, 8, ["image"]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(ButtonPrimary, {
          type: "span",
          icon: "maximize",
          size: "small",
          utilities: "opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90"
        })
      ])
    ], 8, _hoisted_1),
    (__props.caption || __props.credits)
      ? (_openBlock(), _createElementBlock("figcaption", _hoisted_3, [
          (__props.credits)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(__props.credits), 1))
            : _createCommentVNode("", true),
          (__props.caption)
            ? (_openBlock(), _createElementBlock("span", {
                key: 1,
                innerHTML: sanitizedCaption.value
              }, null, 8, _hoisted_5))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("div", {
              class: _normalizeClass(_unref(getClasses)(classes.value, 'overlay')),
              onClick: closeLightbox
            }, null, 2),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(ButtonPrimary, {
                icon: "minus",
                size: "small",
                disabled: zoomLevel.value <= 1,
                onClick: zoomOut
              }, null, 8, ["disabled"]),
              _createElementVNode("span", _hoisted_8, _toDisplayString(Math.round(zoomLevel.value * 100)) + "% ", 1),
              _createVNode(ButtonPrimary, {
                icon: "plus",
                size: "small",
                onClick: zoomIn
              })
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(Body, { color: "text-white" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(getGlobalTranslation)("Press ESC to close")), 1)
                ]),
                _: 1
              }),
              _createVNode(ButtonPrimary, {
                ga: { category: 'popup-hide', action: 'click', label: _unref(getGlobalTranslation)('Close') },
                icon: "times",
                size: "small",
                onClick: closeLightbox
              }, null, 8, ["ga"])
            ]),
            _createElementVNode("div", {
              ref_key: "scrollContainer",
              ref: scrollContainer,
              class: "no-scrollbar pointer-events-auto grid h-full w-full place-items-center overflow-auto p-4 md:p-10",
              style: {"scrollbar-width":"none","-ms-overflow-style":"none"},
              onMousedown: startDrag,
              onMousemove: onDrag,
              onMouseup: stopDrag,
              onMouseleave: stopDrag,
              onClick: _withModifiers(closeLightbox, ["self"])
            }, [
              _createElementVNode("img", {
                src: __props.image.url,
                srcset: imgSrcset.value,
                alt: __props.image.alt || __props.image.title,
                style: _normalizeStyle(imageStyle.value),
                class: "relative z-10 select-none rounded-lg bg-white/5 shadow-2xl transition-all duration-200 ease-out",
                draggable: "false",
                onClick: toggleZoomClick
              }, null, 12, _hoisted_10)
            ], 544)
          ]))
        : _createCommentVNode("", true)
    ]))
  ], 2))
}
}

})